import { Filtering, QSpinner, QText } from '@qualio/ui-components';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTags } from '../../../../../hooks/useTags';
import { keyById } from '../../../../../util/keyById';
import { useTemplates } from '../../../../../hooks/useTemplates';
import { useUsers } from '../../../../../hooks/useUsers';
import TypeItemlabel from '../../../TableFilters/TypeItemLabel';
import { ActiveTagFilter } from '../../../DocumentWorkspace/WorkspaceFilters';
import { trainingFilterLabelKeys } from '../constants';

export const useLibraryFilterDefinitions = () => {
  const { t } = useTranslation();
  const { data: tags = [] } = useTags();
  const { data: templates = [] } = useTemplates({ includeArchived: true });
  const { data: users = [] } = useUsers();

  const tagsMap = useMemo(() => keyById(tags, (tag) => tag.id), [tags]);

  const templatesMap = useMemo(
    () => keyById(templates, (template) => template.prefix),
    [templates],
  );

  const usersMap = useMemo(() => keyById(users, (user) => user.id), [users]);

  const activeTagFilterRender = useCallback(
    (value: unknown) => <ActiveTagFilter value={value} tagsMap={tagsMap} />,
    [tagsMap],
  );

  const activeTemplateFilterRender = useCallback(
    (value: unknown) => {
      const template = templatesMap[String(value)];
      if (template) {
        return (
          <TypeItemlabel
            templateId={template.id}
            templatePrefix={template.prefix}
            templateName={template.name}
            parent={template.parent}
          />
        );
      }
      return <QSpinner size="sm" />;
    },
    [templatesMap],
  );

  const activeOwnerFilterRender = useCallback(
    (value: unknown) => {
      const user = usersMap[Number(value)];
      if (user) {
        return <QText>{user.full_name}</QText>;
      }
      return <QSpinner size="sm" />;
    },
    [usersMap],
  );

  const activeTrainingFilterRender = useCallback(
    (value: unknown) => {
      const labelKey = trainingFilterLabelKeys[String(value)];
      if (labelKey) {
        return <QText>{t(labelKey)}</QText>;
      }
      return <></>;
    },
    [t],
  );

  return useMemo(
    () => ({
      filteringTemplates: {
        label: t('labels.type'),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: activeTemplateFilterRender,
      },
      filteringTag: {
        label: t('labels.tag'),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: activeTagFilterRender,
      },
      owner: {
        label: t('labels.owner'),
        schema: Filtering.schemas.StringSchema().nullish(),
        activeRender: activeOwnerFilterRender,
      },
      filter_by: {
        label: t('labels.filterBy'),
        schema: Filtering.schemas.StringSchema().nullish(),
        activeRender: activeTrainingFilterRender,
      },
    }),
    [
      activeTemplateFilterRender,
      activeTagFilterRender,
      activeOwnerFilterRender,
      activeTrainingFilterRender,
      t,
    ],
  );
};
